import type { FC } from 'react';
import { useRef, useState } from 'react';
import { FaMinusCircle, FaPlusCircle } from 'react-icons/fa';
import { Box } from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view';

import OrgTreeSearch from 'src/components/common/OrgTree/components/OrgTreeSearch';
import { renderTreeItem } from 'src/components/common/OrgTree/helper';
import useOrgTree from 'src/components/common/OrgTree/OrgTreeContext';
import type { OrgTreeState } from 'src/components/common/OrgTree/types';

import useSelectOrgUnit from './hooks/useSelectOrgUnit';

import { styles } from 'src/components/common/OrgTree/index.styles';

export interface OrgTreeProps {
  orgTreeState: OrgTreeState;
  itemsAreDraggable?: boolean;
  syncWithFilter?: boolean;
}

const OrgTreeMultiSelect: FC<OrgTreeProps> = ({
  orgTreeState,
  itemsAreDraggable = false,
  syncWithFilter = true,
}) => {
  const { addSearchedOrgUnit, expanded, expandItems } = useOrgTree();
  const [selected, setSelected] = useState([
    ...(orgTreeState.nodes[0] ? String(orgTreeState.nodes[0].value) : []),
  ]);
  const [isSearching, setIsSearching] = useState(false);
  const rootRef = useRef(null);
  const selectOrgUnit = useSelectOrgUnit(
    selected,
    setSelected,
    orgTreeState,
    syncWithFilter,
  );

  return (
    <Box className="OrgTree-root" sx={styles.orgTree} ref={rootRef}>
      <OrgTreeSearch
        itemsAreDraggable={itemsAreDraggable}
        onSelect={addSearchedOrgUnit(selectOrgUnit, rootRef.current)}
        onSearchStateChange={setIsSearching}
        isSearching={isSearching}
      />
      {!isSearching &&
        orgTreeState.nodes?.map(({ value, label, children }, i) => (
          <SimpleTreeView
            key={i}
            multiSelect
            checkboxSelection
            onItemSelectionToggle={(_, itemId, isSelected) => {
              selectOrgUnit(itemId, isSelected);
            }}
            selectedItems={selected}
            onExpandedItemsChange={expandItems}
            expandedItems={expanded}
            slots={{
              expandIcon: () => <FaPlusCircle size="20px" />,
              collapseIcon: () => <FaMinusCircle size="20px" />,
            }}
          >
            {renderTreeItem(value, label, children, i)}
          </SimpleTreeView>
        ))}
    </Box>
  );
};

export default OrgTreeMultiSelect;
